import type {
    CompanyEmployeeExpandOpts,
    CompanyExpandOpts,
    CompanyMFHRelationExpandOpts,
    CompanyOrContactRelation,
    FetchContractorCapacityWeeksParams,
    ICompany,
    ICompanyEmploymentRelation,
    ICompanyEstateRelation,
    ICompanyMfhRelation,
    IContactPerson,
    IContractorCapacity,
    IContractorCapacityWeek,
    IEstate,
    IFetchCompanyMFHParams,
    IFetchCompanyParams,
    IFindCompanyLinkedEstatesParams,
    IFindSubcompaniesAndEstatesParams,
    IFindSubcompaniesAndEstatesResults,
    IMFH,
    IPaginationFields,
} from '@condo/domain';
import { omit } from 'lodash-es';
import { getClient } from './api-client';

export const fetchCompanies = async (
    companyFetchParams?: IFetchCompanyParams['query'] & { expand: IFetchCompanyParams['expand'] },
): Promise<{ companies: ICompany[]; total: number }> => {
    const { expand, ...query } = companyFetchParams ?? {};

    return getClient('basecamp')
        .get('/companies', { params: { query, expand } })
        .then(response => response.data);
};

export const fetchCompany = async (companyId: number, opts?: { expand?: CompanyExpandOpts }): Promise<ICompany> =>
    getClient('basecamp')
        .get(`/companies/${companyId}`, { params: opts })
        .then(response => response.data.company);

export const fetchCompanyEmployments = async (opts: {
    companyId: number;
    pagination?: IPaginationFields;
    expand?: CompanyEmployeeExpandOpts;
}): Promise<{
    total: number;
    employments: ICompanyEmploymentRelation[];
}> =>
    getClient('basecamp')
        .get(`/companies/${opts.companyId}/employments`, {
            params: {
                ...opts?.pagination,
                expand: opts?.expand,
            },
        })
        .then(response => response.data);

export const fetchSubcompaniesAndEstates = async (params: IFindSubcompaniesAndEstatesParams): Promise<IFindSubcompaniesAndEstatesResults> =>
    getClient('basecamp')
        .get(`/companies/${params.companyId}/subcompanies-and-estates`, { params: omit(params, 'companyId') })
        .then(response => response.data);

export const fetchCompanyLinkedEstates = async (companyId: number, params: IFindCompanyLinkedEstatesParams): Promise<{ estates: IEstate[]; total: number }> => {
    return getClient('basecamp')
        .get(`/companies/${companyId}/estates`, { params })
        .then(response => response.data);
};

export const fetchCompanyLinkedMFHs = async (companyId: number, params?: IFetchCompanyMFHParams): Promise<{ mfhs: IMFH[]; total: number }> => {
    return getClient('basecamp')
        .get(`/companies/${companyId}/mfhs`, { params })
        .then(response => response.data);
};

export const updateCompany = async (
    companyId: number,
    { company, employees, estateId }: { company: ICompany; employees?: Partial<IContactPerson | ICompanyEmploymentRelation>[]; estateId?: number },
): Promise<ICompany> =>
    getClient('basecamp')
        .patch(`/companies/${companyId}`, { company, employees, estateId })
        .then(response => response.data.company);

export const createCompany = async ({
    company,
    employees,
    estateId,
    mfhId,
    relationName,
}: {
    company: ICompany;
    employees?: Partial<IContactPerson | ICompanyEmploymentRelation>[];
    estateId?: number;
    mfhId?: number;
    relationName?: CompanyOrContactRelation;
}): Promise<ICompany> =>
    getClient('basecamp')
        .post('/companies', { company, employees, estateId, mfhId, relationName })
        .then(response => response.data.company);

export const saveCompanyEmploymentRelation = async ({
    companyId,
    contactPersonId,
    isMainContact,
    companyTeam,
    companyRole,
}: ICompanyEmploymentRelation): Promise<ICompanyEmploymentRelation> =>
    getClient('basecamp')
        .post(`/companies/${companyId}/employments/${contactPersonId}`, {
            isMainContact,
            companyTeam,
            companyRole,
        })
        .then(response => response.data.employee);

export const mergeCompanies = async ({
    company,
    companyIds,
    employees,
}: {
    company: ICompany;
    companyIds: number[];
    employees?: Partial<IContactPerson | ICompanyEmploymentRelation>[];
}): Promise<ICompany> =>
    getClient('basecamp')
        .post('/companies/merge', { company, companyIds, employees })
        .then(response => response.data.company);

export const deleteCompanyEmploymentRelation = async ({ companyId, contactPersonId }: ICompanyEmploymentRelation): Promise<void> =>
    getClient('basecamp').delete(`/companies/${companyId}/employments/${contactPersonId}`);

export const removeCompanyEstateAssignment = async ({
    companyId,
    estateId,
    relationName,
}: {
    companyId: number;
    estateId: number;
    relationName: CompanyOrContactRelation;
}): Promise<void> => getClient('basecamp').delete(`/companies/${companyId}/relations/estates/${estateId}`, { params: { relationName } });

export const updateCompanyEstateRelationStatus = async ({
    companyEstateRelationId,
    isActive,
}: {
    companyEstateRelationId: number;
    isActive: boolean;
}): Promise<ICompanyEstateRelation> =>
    getClient('basecamp')
        .patch(`/company-estate/${companyEstateRelationId}/status`, { isActive })
        .then(response => response.data.companyEstateRelation);

export const removeCompanyMFHAssignment = async ({
    companyId,
    mfhId,
    relationName,
}: {
    companyId: number;
    mfhId: number;
    relationName: CompanyOrContactRelation;
}): Promise<void> => getClient('basecamp').delete(`/companies/${companyId}/relations/mfhs/${mfhId}`, { params: { relationName } });

export const fetchContractorCapacityWeeks = async (params: FetchContractorCapacityWeeksParams): Promise<IContractorCapacityWeek[]> => {
    return getClient('basecamp')
        .get<{ contractorCapacityWeeks: IContractorCapacityWeek[] }>('/contractor-capacity-weeks', { params })
        .then(response => response.data.contractorCapacityWeeks);
};

export const fetchContractorCapacities = async (params: { contractorId?: number }): Promise<IContractorCapacity[]> => {
    return getClient('basecamp')
        .get<{ contractorCapacities: IContractorCapacity[] }>(`/companies/contractor-capacities`, { params })
        .then(response => response.data.contractorCapacities);
};

export const saveContractorCapacity = async (data: Partial<IContractorCapacity>): Promise<IContractorCapacity> => {
    return getClient('basecamp')
        .put<{ contractorCapacity: IContractorCapacity }>(`/contractor-capacities`, data)
        .then(response => response.data.contractorCapacity);
};

export const fetchCompanyMFHRelations = async (
    mfhId: number,
    params: { companyId?: number; relationName?: CompanyOrContactRelation } = {},
    expand: CompanyMFHRelationExpandOpts,
): Promise<ICompanyMfhRelation[]> => {
    return getClient('basecamp')
        .get(`/companies/mfhs/${mfhId}/relations`, { params: { ...params, expand } })
        .then(response => response.data);
};

export const updateCompanyMFHRelations = async ({
    mfhId,
    relationName,
    companyIds,
}: {
    mfhId: number;
    relationName: CompanyOrContactRelation;
    companyIds: number[];
}): Promise<void> => {
    return getClient('basecamp').put(`/companies/mfhs/${mfhId}/relations/${relationName}`, {
        companyIds,
    });
};
